import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Search,
	Download,
	Package,
	Building2,
	AlertTriangle,
	TrendingDown,
} from 'lucide-react'

import { cn, downloadCSV } from '../../lib/utils'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import { PageHeader } from '../../components/ui/PageHeader'
import { Card, CardContent } from '../../components/ui/Card'
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from '../../components/ui/Table'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useInventoryBalances, type InventoryBalance } from '../../hooks/useInventory'
import { useActiveOutlets } from '../../hooks/useOutlets'

export default function InventoryPage() {
	const { t, i18n } = useTranslation()

	const [page, setPage] = useState(1)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [searchTerm, setSearchTerm] = useState('')
	const [selectedOutlet, setSelectedOutlet] = useState<string>('')
	const [showLowStockOnly, setShowLowStockOnly] = useState(false)

	const { data: outletsData } = useActiveOutlets()
	const outlets = outletsData?.data || []

	const {
		data: inventoryData,
		isLoading,
		error,
	} = useInventoryBalances({
		page,
		limit: 20,
		outletCode: selectedOutlet || undefined,
		search: searchTerm || undefined,
		minQty: showLowStockOnly ? 0 : undefined,
	})

	const inventory = inventoryData?.data || []
	const pagination = inventoryData?.pagination

	const handleExportCSV = useCallback(() => {
		const exportData = inventory.map((item: InventoryBalance) => ({
			[t('inventory.fields.productCode', 'Product Code')]: item.productCode,
			[t('inventory.fields.productName', 'Product Name')]: i18n.language === 'ar' ? item.productNameAr || item.productName : item.productName,
			[t('inventory.fields.outlet', 'Outlet')]: item.outletName || item.outletCode,
			[t('inventory.fields.currentQty', 'Current Qty')]: item.balances.currentQty,
			[t('inventory.fields.averageCost', 'Avg Cost')]: item.balances.averageCost.toFixed(2),
			[t('inventory.fields.totalValue', 'Total Value')]: item.balances.totalValue.toFixed(2),
		}))

		downloadCSV(exportData, `inventory_${new Date().toISOString().split('T')[0]}`)
	}, [inventory, t, i18n])

	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleSearch = useCallback((value: string) => {
		setSearchInputValue(value)
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}
		searchTimeoutRef.current = setTimeout(() => {
			setSearchTerm(value)
			setPage(1)
		}, 300)
	}, [])

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	const getStockStatus = (currentQty: number, minQty?: number, criticalQty?: number) => {
		if (currentQty <= 0) return { status: 'out', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: t('inventory.status.outOfStock', 'Out of Stock') }
		if (criticalQty && currentQty <= criticalQty) return { status: 'critical', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: t('inventory.status.critical', 'Critical') }
		if (minQty && currentQty <= minQty) return { status: 'low', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: t('inventory.status.low', 'Low') }
		return { status: 'normal', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: t('inventory.status.normal', 'Normal') }
	}

	if (isLoading && !inventory.length) {
		return (
			<div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
				<LoadingSpinner size="lg" />
			</div>
		)
	}

	if (error) {
		const errorMessage = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
		return (
			<div className="flex flex-col items-center justify-center min-h-[400px] text-center" role="alert">
				<Package className="h-12 w-12 text-gray-400 mb-4" />
				<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
					{t('common.error', 'Error')}
				</h3>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{errorMessage}</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title={t('inventory.title', 'Inventory')}
				subtitle={t('inventory.subtitle', 'View stock levels and inventory balances')}
			/>

			{/* Summary Cards */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('inventory.totalItems', 'Total Items')}
								</p>
								<p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
									{inventory.length}
								</p>
							</div>
							<Package className="h-8 w-8 text-blue-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('inventory.lowStock', 'Low Stock')}
								</p>
								<p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
									{inventory.filter((item: InventoryBalance) => item.balances.currentQty < 10).length}
								</p>
							</div>
							<AlertTriangle className="h-8 w-8 text-yellow-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('inventory.totalValue', 'Total Value')}
								</p>
								<p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
									{inventory.reduce((sum, item) => sum + item.balances.totalValue, 0).toLocaleString(
										i18n.language === 'ar' ? 'ar-EG' : 'en-US',
										{ minimumFractionDigits: 2 }
									)}
								</p>
							</div>
							<TrendingDown className="h-8 w-8 text-green-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('inventory.outlets', 'Outlets')}
								</p>
								<p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
									{outlets.length}
								</p>
							</div>
							<Building2 className="h-8 w-8 text-purple-500" />
						</div>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardContent className="p-0">
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
							<div className="relative flex-1 max-w-md">
								<Input
									type="text"
									placeholder={t('inventory.searchPlaceholder', 'Search products...')}
									value={searchInputValue}
									onChange={(e) => handleSearch(e.target.value)}
									leftIcon={<Search className="h-4 w-4" />}
								/>
							</div>
							<div className="flex gap-2">
								<select
									value={selectedOutlet}
									onChange={(e) => {
										setSelectedOutlet(e.target.value)
										setPage(1)
									}}
									className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm"
								>
									<option value="">{t('inventory.allOutlets', 'All Outlets')}</option>
									{outlets.map((outlet) => (
										<option key={outlet.id || outlet.code} value={outlet.code}>
											{i18n.language === 'ar' ? outlet.nameAr || outlet.name : outlet.name}
										</option>
									))}
								</select>
								<Button
									variant={showLowStockOnly ? 'primary' : 'outline'}
									onClick={() => {
										setShowLowStockOnly(!showLowStockOnly)
										setPage(1)
									}}
									leftIcon={<AlertTriangle className="h-4 w-4" />}
								>
									{t('inventory.lowStockOnly', 'Low Stock Only')}
								</Button>
								<Button variant="outline" onClick={handleExportCSV} leftIcon={<Download className="h-4 w-4" />}>
									{t('common.exportCSV', 'Export CSV')}
								</Button>
							</div>
						</div>
					</div>

					{inventory.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<Package className="h-12 w-12 text-gray-400 mb-4" />
							<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
								{t('inventory.noItems', 'No inventory items found')}
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
								{t('inventory.noItemsDescription', 'No inventory data available for the selected filters.')}
							</p>
						</div>
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('inventory.fields.productCode', 'Product Code')}</TableHead>
										<TableHead>{t('inventory.fields.productName', 'Product Name')}</TableHead>
										<TableHead>{t('inventory.fields.outlet', 'Outlet')}</TableHead>
										<TableHead>{t('inventory.fields.currentQty', 'Current Qty')}</TableHead>
										<TableHead>{t('inventory.fields.averageCost', 'Avg Cost')}</TableHead>
										<TableHead>{t('inventory.fields.totalValue', 'Total Value')}</TableHead>
										<TableHead>{t('inventory.fields.status', 'Status')}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{inventory.map((item: InventoryBalance) => {
										const stockStatus = getStockStatus(item.balances.currentQty)
										return (
											<TableRow key={`${item.productCode}-${item.outletCode}`}>
												<TableCell className="font-medium font-mono">{item.productCode}</TableCell>
												<TableCell>
													{i18n.language === 'ar' ? item.productNameAr || item.productName : item.productName}
												</TableCell>
												<TableCell>{item.outletName || item.outletCode}</TableCell>
												<TableCell>
													{item.balances.currentQty.toLocaleString(
														i18n.language === 'ar' ? 'ar-EG' : 'en-US',
														{ minimumFractionDigits: 2 }
													)}
												</TableCell>
												<TableCell>
													{item.balances.averageCost.toLocaleString(
														i18n.language === 'ar' ? 'ar-EG' : 'en-US',
														{ minimumFractionDigits: 2 }
													)}
												</TableCell>
												<TableCell>
													{item.balances.totalValue.toLocaleString(
														i18n.language === 'ar' ? 'ar-EG' : 'en-US',
														{ minimumFractionDigits: 2 }
													)}
												</TableCell>
												<TableCell>
													<span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', stockStatus.color)}>
														{stockStatus.label}
													</span>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>

							{pagination && pagination.totalPages > 1 && (
								<nav
									className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700"
									aria-label={t('pagination.navigation', 'Pagination navigation')}
								>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										{t('common.showing', 'Showing')} {(page - 1) * 20 + 1}-
										{Math.min(page * 20, pagination.total)} {t('common.of', 'of')} {pagination.total}
									</div>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
										>
											{t('common.previous', 'Previous')}
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
											disabled={page === pagination.totalPages}
										>
											{t('common.next', 'Next')}
										</Button>
									</div>
								</nav>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
